import { useEffect, useMemo, useState, Fragment } from "react";
import { api, formatINR, downloadReport } from "@/lib/api";
import { PageHeader, Card, StatusBadge, EmptyState } from "@/components/ui-helpers";
import { Plus, Search, Download, FileSpreadsheet, FileText, X, History, Lock, AlertTriangle, RotateCcw, CheckCircle2, MoreHorizontal, Wallet, Landmark, PiggyBank, ClipboardCheck, Pencil, Trash2 } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useBranch } from "@/contexts/BranchContext";
import { BranchSelect } from "@/components/BranchSelect";
import { BranchFilter } from "@/components/BranchFilter";
import { BulkUploadButtons, BulkUploadResultBanner } from "@/components/BulkUploadControls";
import { usePaginatedList } from "@/hooks/usePaginatedList";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { ProjectOpeningBalanceDialog } from "@/components/ProjectOpeningBalanceDialog";
import { useMastersRefresh } from "@/hooks/useMastersRefresh";
import { PaginationControls } from "@/components/PaginationControls";
import UserLabel, { useUserDirectory } from "@/components/UserLabel";
import { useFormTab } from "@/hooks/useFormTab";
import ProjectWorkspace from "@/components/ProjectWorkspace";
import { OpsSwitchConfirm } from "@/components/OpsSwitchConfirm";

const STATUSES = ["Active", "Completed", "Hold"];

export default function Projects() {
  useUserDirectory();
  const [search, setSearch] = useState("");
  const [filterBranch, setFilterBranch] = useState("");
  // Sprint 2026-Q1 — Budget Set / Not Set filter.
  // "" = All · "set" = effective budget > 0 · "unset" = no budget rows / 0.
  const [filterBudget, setFilterBudget] = useState("");
  // Sprint 2026-Q1 — Project Budget Lock filter.
  // "" = All · "on" = enforce_budget_lock=true · "off" = false/undefined.
  const [filterLock, setFilterLock] = useState("");
  // Sprint 2026-Q1d / Asset Depreciation Step ① — Project Group filter.
  // "" = All groups · "<group_id>" = single group.
  const [filterGroup, setFilterGroup] = useState("");
  const [filterOps, setFilterOps] = useState("");
  // Project Group master, loaded once for the filter dropdown. Sorted
  // by sort_order so the most-used group leads.
  const [projectGroups, setProjectGroups] = useState([]);
  // Per-project effective budget totals { project_id: amount }. Loaded
  // once on mount via `/project-budgets/summary` so the list page
  // doesn't fire N HTTP calls.
  const [budgetTotals, setBudgetTotals] = useState({});
  // Sprint 2026-Q1d / Asset Depreciation Step ③ — Per-project closure
  // warning counts { project_id: open_item_count }. Same pattern as
  // budgetTotals — single bulk endpoint, then the table renders inline
  // warning badges without N HTTP calls. A non-zero count means the
  // project has open transactional items (returnables, repairs, unpaid
  // PIs, draft estimates, or issued assets) that closure should review.
  const [closureWarnings, setClosureWarnings] = useState({});

  const debouncedSearch = useDebouncedValue(search, 300);
  // Jun-2026 — all-pages search: `q` is matched server-side against
  // project_code / project_name / client_name / PM / division, so rows on
  // other pages are found too (list is paginated).
  const params = useMemo(() => {
    const p = {};
    if (filterBranch) p.branch_id = filterBranch;
    if (debouncedSearch.trim()) p.q = debouncedSearch.trim();
    return p;
  }, [filterBranch, debouncedSearch]);
  const { items: list, total, page, pageSize, loading, setPage, setPageSize, reload } = usePaginatedList("/projects", params);
  const load = reload;
  const openFormTab = useFormTab();
  const { user, hasPerm } = useAuth();
  // UX-1 — quick "Operational Project" toggle straight from the list; uses
  // the same controlled switch (mandatory reason + safe-disable impact).
  const canOpsAdmin = user?.role === "admin" || hasPerm("project_ops_admin", "edit");
  const [opsSwitch, setOpsSwitch] = useState(null);
  // Jun-2026 — Opening Balances (previous-ERP carry-in; P&L-only).
  const [openingFor, setOpeningFor] = useState(null);
  // Aug-2026 — Phase 1: Project Scope & BOQ. Global Project-Ops flag; the
  // per-row BOQ action shows only when this AND the project's own
  // project_ops_enabled flag AND the project_boq permission all hold.
  const [opsGlobalOn, setOpsGlobalOn] = useState(false);

  // Jul-2026 — Tabs keep this page mounted (display:none) when hidden, so a
  // bulk project rename done in Data Tools left this list stale. Re-fetch
  // whenever the global masters:refresh event fires.
  const mastersRefreshKey = useMastersRefresh();
  useEffect(() => {
    if (mastersRefreshKey) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mastersRefreshKey]);

  useEffect(() => {
    api.get("/project-budgets/summary")
      .then(r => setBudgetTotals(r.data?.totals || {}))
      .catch(() => setBudgetTotals({}));
    // Sprint 2026-Q1d Step ③ — bulk closure-warning totals for the badge.
    api.get("/project-closures/_summary")
      .then(r => setClosureWarnings(r.data?.totals || {}))
      .catch(() => setClosureWarnings({}));
    // Sprint 2026-Q1d — Project Group master for the filter dropdown.
    api.get("/project-groups")
      .then(r => {
        const rows = (r.data || []).filter(g => g.is_active !== false)
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)
                          || (a.name || "").localeCompare(b.name || ""));
        setProjectGroups(rows);
      })
      .catch(() => setProjectGroups([]));
    api.get("/project-ops/status")
      .then(r => setOpsGlobalOn(!!r.data?.global_enabled))
      .catch(() => setOpsGlobalOn(false));
  }, []);

  const openNew = () => openFormTab({
    path: "/projects/new",
    label: "New Project",
    icon: Plus,
    parent: "/projects",
    onSaved: load,
    render: ({ close }) => <ProjectModal project={null} onClose={close} onSaved={close} />,
  });
  const openEdit = (p) => openFormTab({
    path: `/projects/edit/${p.id}`,
    label: `Edit · ${p.project_code || p.project_name || "Project"}`,
    parent: "/projects",
    onSaved: load,
    render: ({ close }) => <ProjectModal project={p} onClose={close} onSaved={close} />,
  });
  const openCost = (p) => openFormTab({
    path: `/projects/cost-summary/${p.id}`,
    label: `Cost · ${p.project_code || "Project"}`,
    parent: "/projects",
    render: ({ close }) => <ProjectCostSummaryTab project={p} onClose={close} />,
  });
  const openBudget = (p) => openFormTab({
    path: `/projects/budget/${p.id}`,
    label: `Budget · ${p.project_code || "Project"}`,
    parent: "/projects",
    render: ({ close }) => <ProjectBudgetTab project={p} onClose={close} />,
  });
  // Aug-2026 — Project Workspace: clicking the Project Code/Name opens ONE
  // dedicated tab with an inner nav bar (BOQ / Vendor Management / Work
  // Progress / Project Stock). All project-operations modules live here.
  const openWorkspace = (p) => openFormTab({
    path: `/projects/workspace/${p.id}`,
    label: `${p.project_code || "Project"} · Workspace`,
    parent: "/projects",
    render: ({ close }) => <ProjectWorkspace project={p} opsGlobalOn={opsGlobalOn} onClose={close} />,
  });
  // Sprint 2026-Q1d Step ③ — Closure Wizard tab. Refreshes both the
  // budget summary AND the warning badges on close so the listing-page
  // table reflects the latest status / counts after a close-or-reopen.
  const reloadBadges = () => {
    api.get("/project-closures/_summary")
      .then(r => setClosureWarnings(r.data?.totals || {}))
      .catch(() => {});
  };
  const openClosure = (p) => openFormTab({
    path: `/projects/closure/${p.id}`,
    label: `Closure · ${p.project_code || "Project"}`,
    parent: "/projects",
    onSaved: () => { load(); reloadBadges(); },
    render: ({ close }) => (
      <ProjectClosureWizardTab
        project={p}
        onClose={() => { reloadBadges(); close(); }}
        onSaved={() => { load(); reloadBadges(); }}
      />
    ),
  });

  const filtered = list.filter(p => {
    if (filterBranch && p.branch_id !== filterBranch) return false;
    if (search) {
      const q = search.toLowerCase();
      const hit = (
        p.project_name?.toLowerCase().includes(q) ||
        p.project_code?.toLowerCase().includes(q) ||
        p.client_name?.toLowerCase().includes(q)
      );
      if (!hit) return false;
    }
    if (filterBudget) {
      const amt = Number(budgetTotals[p.id] || 0);
      if (filterBudget === "set" && !(amt > 0)) return false;
      if (filterBudget === "unset" && amt > 0) return false;
    }
    if (filterLock) {
      const locked = !!p.enforce_budget_lock;
      if (filterLock === "on" && !locked) return false;
      if (filterLock === "off" && locked) return false;
    }
    if (filterGroup && (p.project_group_id || "") !== filterGroup) return false;
    if (filterOps) {
      const opsOn = !!p.project_ops_enabled;
      if (filterOps === "on" && !opsOn) return false;
      if (filterOps === "off" && opsOn) return false;
    }
    return true;
  });

  async function handleDelete(id) {
    if (!window.confirm("Delete this project?")) return;
    await api.delete(`/projects/${id}`);
    toast.success("Project deleted");
    load();
  }

  return (
    <div>
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="Master Data"
        title="Projects"
        subtitle="All civil, electrical, solar and IFMS engagements at a glance."
        actions={
          <>
            <BulkUploadButtons master="projects" filename="projects_master_template.xlsx" onUploaded={load} />
            <button data-testid="export-xlsx-btn" onClick={() => downloadReport("projects", "xlsx")} className="bg-white border border-slate-300 px-4 py-2.5 text-xs uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-2">
              <FileSpreadsheet size={14} /> Excel
            </button>
            <button data-testid="export-pdf-btn" onClick={() => downloadReport("projects", "pdf")} className="bg-white border border-slate-300 px-4 py-2.5 text-xs uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-2">
              <FileText size={14} /> PDF
            </button>
            <button data-testid="add-project-btn" onClick={openNew} className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
              <Plus size={14} /> New Project
            </button>
          </>
        }
      />

      <BulkUploadResultBanner master="projects" />

      <div className="mb-4 flex items-center gap-3 max-w-3xl flex-wrap">
        <div className="relative flex-1 max-w-md min-w-[220px]">
          <Search size={15} className="absolute left-3 top-3 text-slate-400" />
          <input data-testid="search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, code, client…"
            className="w-full border border-slate-300 bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#F97316]" />
        </div>
        <BranchFilter value={filterBranch} onChange={setFilterBranch} testid="projects-filter-branch" />
        <select
          data-testid="projects-filter-budget"
          value={filterBudget}
          onChange={(e) => setFilterBudget(e.target.value)}
          className="border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#F97316]"
          title="Filter by whether a budget has been configured for the project"
        >
          <option value="">All Budgets</option>
          <option value="set">Budget Set</option>
          <option value="unset">Budget Not Set</option>
        </select>
        <select
          data-testid="projects-filter-lock"
          value={filterLock}
          onChange={(e) => setFilterLock(e.target.value)}
          className="border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#F97316]"
          title="Filter by Purchase Voucher lock status (Admin-controlled)"
        >
          <option value="">All Locks</option>
          <option value="on">Lock: Enabled</option>
          <option value="off">Lock: Disabled</option>
        </select>
        <select
          data-testid="projects-filter-group"
          value={filterGroup}
          onChange={(e) => setFilterGroup(e.target.value)}
          className="border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#F97316]"
          title="Filter by Project Group (Revenue / Internal Cost / Corporate / Capital)"
        >
          <option value="">All Groups</option>
          {projectGroups.map(g => (
            <option key={g.id} value={g.id}>{g.name}</option>
          ))}
        </select>
        <select
          data-testid="projects-filter-ops"
          value={filterOps}
          onChange={(e) => setFilterOps(e.target.value)}
          className="border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#F97316]"
          title="Filter by Project Operations status (Ops On / Ops Off)"
        >
          <option value="">All Ops</option>
          <option value="on">Ops: On</option>
          <option value="off">Ops: Off</option>
        </select>
      </div>

      {filtered.length === 0 ? (
        <EmptyState title="No projects yet" hint="Click 'New Project' to create your first one." />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full table-fixed min-w-[1080px] lg:min-w-0" data-testid="projects-table">
              {/* Fixed column widths so the grid fits a single viewport with no horizontal scroll.
                  Long text columns (Project, Client, Manager) truncate with a title tooltip. */}
              <colgroup>
                <col className="w-[6%]"  />{/* Code */}
                <col className="w-[21%]" />{/* Project */}
                <col className="w-[14%]" />{/* Client */}
                <col className="w-[6%]"  />{/* Type */}
                <col className="w-[8%]"  />{/* Group */}
                <col className="w-[9%]"  />{/* Manager */}
                <col className="w-[7%]"  />{/* Value */}
                <col className="w-[7%]"  />{/* Budget */}
                <col className="w-[5%]"  />{/* Lock */}
                <col className="w-[10%]" />{/* Status */}
                <col className="w-[7%]"  />{/* Actions */}
              </colgroup>
              <thead className="bg-slate-50 border-b-2 border-slate-200">
                <tr>
                  {["Code", "Project", "Client", "Type", "Group", "Manager", "Value", "Budget Amount", "Lock", "Status", "Actions"].map(h =>
                    <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2 px-2 leading-tight">{h}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => {
                  const budgetAmt = Number(budgetTotals[p.id] || 0);
                  const hasBudget = budgetAmt > 0;
                  return (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="py-2 px-2 text-[11px] font-bold truncate" title={p.project_code}>
                      <button data-testid={`project-code-link-${p.id}`} onClick={() => openWorkspace(p)}
                        title="Open Project Workspace"
                        className="text-[#0B1F3A] hover:text-[#F97316] hover:underline font-bold truncate max-w-full text-left">
                        {p.project_code}
                      </button>
                    </td>
                    <td className="py-2 px-2 text-xs text-slate-800 overflow-hidden">
                      <button data-testid={`project-name-link-${p.id}`} onClick={() => openWorkspace(p)}
                        title="Open Project Workspace"
                        className="font-medium whitespace-normal break-words max-w-full text-left text-slate-800 hover:text-[#F97316] hover:underline block leading-snug">
                        {p.project_name}
                      </button>
                      {p.division && <div className="text-[10px] text-slate-500 truncate" title={p.division}>{p.division}</div>}
                    </td>
                    <td className="py-2 px-2 text-xs text-slate-700 truncate" title={p.client_name}>{p.client_name}</td>
                    <td className="py-2 px-2 text-[10px] uppercase text-slate-700 truncate" title={p.work_type}>{p.work_type}</td>
                    <td className="py-2 px-2 text-[10px] overflow-hidden" data-testid={`project-group-${p.id}`}>
                      {p.project_group_name ? (
                        <span
                          className="inline-flex max-w-full px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold bg-slate-100 text-slate-700 border border-slate-200 rounded truncate"
                          title={p.project_group_name}
                        >
                          {p.project_group_name}
                        </span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">—</span>
                      )}
                    </td>
                    <td className="py-2 px-2 text-xs text-slate-700 truncate" title={p.project_manager || ""}>{p.project_manager || "—"}</td>
                    <td className="py-2 px-2 text-xs tabular text-slate-800 truncate text-right" title={formatINR(p.project_value)}>{formatINR(p.project_value)}</td>
                    <td className="py-2 px-2 text-xs tabular truncate text-right" data-testid={`budget-amount-${p.id}`} title={hasBudget ? formatINR(budgetAmt) : "Not Set"}>
                      {hasBudget ? (
                        <span className="font-bold text-emerald-700">{formatINR(budgetAmt)}</span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Not Set</span>
                      )}
                    </td>
                    <td className="py-2 px-2" data-testid={`lock-status-${p.id}`}>
                      {p.enforce_budget_lock ? (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold bg-amber-100 text-amber-800 border border-amber-300 rounded"
                          title="Budget lock enabled"
                        >
                          <Lock size={9} /> On
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold bg-slate-100 text-slate-500 border border-slate-200 rounded"
                          title="Budget lock disabled"
                        >
                          Off
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-2 overflow-hidden">
                      <div className="flex items-center gap-1 min-w-0">
                        <StatusBadge status={p.status} />
                        {(p.project_ops_enabled || canOpsAdmin) && (
                          <button type="button" data-testid={`ops-toggle-${p.id}`}
                            disabled={!canOpsAdmin}
                            onClick={() => canOpsAdmin && setOpsSwitch(p)}
                            title={p.project_ops_enabled
                              ? (canOpsAdmin ? "Operational Project — click to disable (history stays read-only)" : "Operational Project")
                              : "Mark as Operational Project (enables BOQ / Vendor Management / GRN / Stock / Work Progress)"}
                            className={`inline-flex items-center px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold border rounded shrink-0 ${
                              p.project_ops_enabled
                                ? `bg-emerald-100 text-emerald-800 border-emerald-300${canOpsAdmin ? " hover:bg-emerald-200" : ""}`
                                : "bg-slate-100 text-slate-500 border-slate-300 hover:bg-slate-200"}`}>
                            Ops {p.project_ops_enabled ? "On" : "Off"}
                          </button>
                        )}
                        {/* Jun-2026 (user request) — inline warning chip removed;
                            the open-items count now shows inside the Actions
                            menu next to Closure Review. */}
                      </div>
                    </td>
                    <td className="py-2 px-2 text-[11px] whitespace-nowrap">
                      {/* Jun-2026 — actions consolidated into one compact popup menu. */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button data-testid={`actions-menu-${p.id}`} title="Project actions"
                            className="inline-flex items-center gap-1 px-2 py-1 border border-slate-300 rounded text-slate-600 hover:border-[#F97316] hover:text-[#F97316] font-bold text-[10px] uppercase tracking-wider transition-colors">
                            <MoreHorizontal size={13} /> Actions
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuItem data-testid={`cost-${p.id}`} onClick={() => openCost(p)} className="gap-2 text-xs cursor-pointer">
                            <Wallet size={13} className="text-[#0B1F3A]" /> Cost Summary
                          </DropdownMenuItem>
                          {hasPerm("project_opening_balance", "view") && (
                            <DropdownMenuItem data-testid={`opening-${p.id}`} onClick={() => setOpeningFor(p)} className="gap-2 text-xs cursor-pointer">
                              <Landmark size={13} className="text-purple-700" /> Opening Balance (prev. ERP)
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem data-testid={`budget-${p.id}`} onClick={() => openBudget(p)} className="gap-2 text-xs cursor-pointer">
                            <PiggyBank size={13} className="text-emerald-700" /> Budget
                          </DropdownMenuItem>
                          <DropdownMenuItem data-testid={`closure-${p.id}`} onClick={() => openClosure(p)} className="gap-2 text-xs cursor-pointer">
                            <ClipboardCheck size={13} className="text-indigo-700" /> Closure Review
                            {closureWarnings[p.id] > 0 && (
                              <span data-testid={`closure-warning-${p.id}`} title={`${closureWarnings[p.id]} open item(s) blocking closure`}
                                className="ml-auto inline-flex items-center gap-0.5 px-1 py-0.5 text-[9px] font-bold bg-amber-100 text-amber-800 border border-amber-300 rounded">
                                <AlertTriangle size={9} /> {closureWarnings[p.id]}
                              </span>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem data-testid={`edit-${p.id}`} onClick={() => openEdit(p)} className="gap-2 text-xs cursor-pointer">
                            <Pencil size={13} className="text-[#F97316]" /> Edit Project
                          </DropdownMenuItem>
                          <DropdownMenuItem data-testid={`delete-${p.id}`} onClick={() => handleDelete(p.id)} className="gap-2 text-xs cursor-pointer text-red-600 focus:text-red-600">
                            <Trash2 size={13} /> Delete Project
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <PaginationControls page={page} pageSize={pageSize} total={total} loading={loading} onPage={setPage} onPageSize={setPageSize} />
        </Card>
      )}
      {openingFor && (
        <ProjectOpeningBalanceDialog
          project={openingFor}
          onClose={() => setOpeningFor(null)}
          onSaved={() => {}} />
      )}
      {opsSwitch && (
        <OpsSwitchConfirm
          title={`${opsSwitch.project_code} — turn Project Operations ${opsSwitch.project_ops_enabled ? "OFF" : "ON"}?${
            !opsSwitch.project_ops_enabled && !opsGlobalOn ? " (note: the Global switch is currently OFF)" : ""}`}
          turningOff={!!opsSwitch.project_ops_enabled}
          impactParams={{ scope: "project", project_id: opsSwitch.id }}
          onConfirm={async (reason) => {
            try {
              await api.put(`/project-ops/projects/${opsSwitch.id}`, {
                enabled: !opsSwitch.project_ops_enabled, reason,
                request_id: crypto.randomUUID(),
              });
              toast.success(!opsSwitch.project_ops_enabled
                ? `${opsSwitch.project_code} is now an Operational Project`
                : `Project Operations disabled for ${opsSwitch.project_code} — history stays read-only`);
              setOpsSwitch(null);
              load();
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Switch failed");
            }
          }}
          onClose={() => setOpsSwitch(null)} />
      )}
    </div>
  );
}

function ProjectModal({ project, onClose, onSaved }) {
  const { active: activeBranch } = useBranch();
  const { user } = useAuth();
  const isAdmin = (user?.role || "").toLowerCase() === "admin";
  // Seed-up the contracts editor:
  //   • New project → start with one empty contract row, marked current.
  //   • Existing project with the new `contracts` array → use it as-is.
  //   • Existing legacy project (only flat fields) → build a single
  //     contract row from those flat fields so the user can immediately
  //     edit / add more without losing their current data.
  const _seedContracts = (p) => {
    if (Array.isArray(p?.contracts) && p.contracts.length > 0) return p.contracts;
    return [{
      id: "", contract_number: p?.project_contract_number || "",
      start_date: p?.start_date || "", end_date: p?.end_date || "",
      project_value: p?.project_value || 0,
      is_current: true, remarks: "",
      bg_created: false, bg_bank_name: "", bg_number: "",
      bg_amount: 0, bg_expiry_date: "", bg_claim_expiry_date: "",
      bg_fd_number: "",
    }];
  };
  const [form, setForm] = useState(project ? { ...project, authorized_users: project.authorized_users || [], contracts: _seedContracts(project) } : {
    project_code: "", project_name: "", client_name: "", division: "",
    branch_id: activeBranch?.id || "", branch_name: activeBranch?.branch_name || "",
    work_type: "Civil",
    project_manager: "", status: "Active", notes: "",
    client_poc: "", ro_number: "", ro_status: "",
    project_start_date: "", project_end_date: "", project_address: "",
    enforce_budget_lock: false,
    project_group_id: "", project_group_name: "",
    authorized_users: [],
    contracts: [{
      id: "", contract_number: "",
      start_date: "", end_date: "", project_value: 0,
      is_current: true, remarks: "",
      bg_created: false, bg_bank_name: "", bg_number: "",
      bg_amount: 0, bg_expiry_date: "", bg_claim_expiry_date: "",
      bg_fd_number: "",
    }],
  });
  const [saving, setSaving] = useState(false);
  const [workTypes, setWorkTypes] = useState([]);
  // Sprint 2026-Q1e — Project-wise User Access Control · Phase 3 · Frontend
  // The Authorized Users multi-select. Candidates are ACTIVE users whose
  // `allowed_branch_ids` covers the currently-picked branch (or admins /
  // view_all_projects power-users). Refreshed whenever the branch changes.
  // Legacy behaviour preserved: an empty list continues to mean
  // "any user in the same branch can access this project".
  const [authCandidates, setAuthCandidates] = useState([]);
  const [authCandidatesLoading, setAuthCandidatesLoading] = useState(false);
  // Sprint 2026-Feb-10i — Search filter for the Authorized Users
  // multi-select. Applied client-side over the already-loaded
  // `authCandidates` array; no debounce needed because the list is
  // small (≤ a few hundred users) and the filter is O(n).
  const [authUserSearch, setAuthUserSearch] = useState("");
  // Sprint 2026-Q1d — Project Group master loaded once per modal open.
  // The dropdown is mandatory but defaults server-side to "Revenue Project"
  // if the field is blank, so the form doesn't block submission for users
  // who skip it. Sorted by `sort_order` so Finance can pin the most-used
  // group on top regardless of alphabetical order.
  const [projectGroups, setProjectGroups] = useState([]);
  // Client Master library — populates the searchable datalist on the
  // Client Name input so operators don't have to remember exact spelling
  // and can't accidentally create a typo'd client that doesn't match an
  // existing master record.
  const [clients, setClients] = useState([]);
  const set = (k, v) => setForm({ ...form, [k]: v });

  // ──────────────────────────────────────────────────────────────────────
  // Contracts editor — multi-row, exactly-one-current. Keep the helpers
  // local since they only mutate `form.contracts`.
  // ──────────────────────────────────────────────────────────────────────
  const updateContract = (idx, patch) => {
    setForm(f => ({
      ...f,
      contracts: f.contracts.map((c, i) => i === idx ? { ...c, ...patch } : c),
    }));
  };
  const markContractCurrent = (idx) => {
    setForm(f => ({
      ...f,
      contracts: f.contracts.map((c, i) => ({ ...c, is_current: i === idx })),
    }));
  };
  const addContract = () => {
    setForm(f => ({
      ...f,
      contracts: [...f.contracts, {
        id: "", contract_number: "",
        start_date: "", end_date: "", project_value: 0,
        is_current: false, remarks: "",
        bg_created: false, bg_bank_name: "", bg_number: "",
        bg_amount: 0, bg_expiry_date: "", bg_claim_expiry_date: "",
        bg_fd_number: "",
      }],
    }));
  };
  const removeContract = (idx) => {
    setForm(f => {
      const remaining = f.contracts.filter((_, i) => i !== idx);
      if (remaining.length === 0) {
        // Always keep at least one row so the editor isn't empty.
        return f;
      }
      // If the deleted row was the current one, promote the first remaining row.
      if (!remaining.some(c => c.is_current)) remaining[0].is_current = true;
      return { ...f, contracts: remaining };
    });
  };

  // Fetch the admin-extensible Work Types master on mount. We tolerate the
  // request failing (e.g. permission denied) by falling back to whatever
  // the project already has set, so the form is never blocked.
  useEffect(() => {
    api.get("/work-types").then(r => {
      const names = (r.data || []).map(w => w.name).filter(Boolean);
      setWorkTypes(names);
    }).catch(() => setWorkTypes([]));
    // Sprint 2026-Q1d — Project Group master. Tolerate failure
    // gracefully so a permission error never blocks the modal.
    api.get("/project-groups")
      .then(r => {
        const rows = (r.data || []).filter(g => g.is_active !== false)
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)
                          || (a.name || "").localeCompare(b.name || ""));
        setProjectGroups(rows);
      })
      .catch(() => setProjectGroups([]));
    // Clients master — branch-scoped to whatever branch the form is
    // currently set to so an operator in Mumbai sees Mumbai clients
    // first. Re-fetched on branch change so switching the project's
    // branch refreshes the dropdown options.
    const params = form.branch_id ? { params: { branch_id: form.branch_id } } : {};
    api.get("/clients", params).then(r => setClients(r.data || []))
      .catch(() => setClients([]));
    // Sprint 2026-Q1e — Fetch authorized-user candidates for the picked
    // branch. Backend endpoint returns ACTIVE users whose branch scope
    // covers this branch (empty allowed_branch_ids = unrestricted). If
    // no branch is picked yet, clear the candidate list.
    if (form.branch_id) {
      setAuthCandidatesLoading(true);
      // Reset the search filter when branch changes so a stale query
      // from a previous branch doesn't hide the fresh candidates.
      setAuthUserSearch("");
      api.get("/authorized-user-candidates", { params: { branch_id: form.branch_id } })
        .then(r => {
          // Backend returns `{rows: [...]}` (paginated envelope) — unwrap if present.
          const rows = Array.isArray(r.data) ? r.data : (r.data?.rows || []);
          setAuthCandidates(rows);
        })
        .catch(() => setAuthCandidates([]))
        .finally(() => setAuthCandidatesLoading(false));
    } else {
      setAuthCandidates([]);
    }
  }, [form.branch_id]);

  async function handleWorkTypeChange(value) {
    if (value !== "__add_new__") { set("work_type", value); return; }
    const raw = window.prompt("Add a new Work Type (e.g. HVAC, Plumbing, Networking):");
    const name = (raw || "").trim();
    if (!name) return;
    const clash = workTypes.find(w => w.toLowerCase() === name.toLowerCase());
    if (clash) {
      toast.info(`"${clash}" already exists — selected.`);
      set("work_type", clash);
      return;
    }
    try {
      await api.post("/work-types", { name, description: "" });
      setWorkTypes(prev => [...prev, name].sort((a, b) => a.localeCompare(b)));
      set("work_type", name);
      toast.success(`Work Type "${name}" added`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add work type");
    }
  }

  async function save(e) {
    e.preventDefault();
    if (!form.branch_id) { toast.error("Branch is mandatory"); return; }
    if (form.project_start_date && form.project_end_date
        && form.project_end_date < form.project_start_date) {
      toast.error("Project End Date cannot be before the Start Date");
      return;
    }
    // Validate at least one contract has a non-blank contract number. We
    // only enforce this on CREATE — legacy projects that pre-date the
    // multi-contract editor have no contract rows and must still be
    // editable (otherwise users can't toggle Lock / change Status / etc.
    // on those rows). Server-side, `project_contract_number` is optional,
    // so skipping the check on edit is safe.
    if (!project) {
      const hasAnyContract = (form.contracts || []).some(c => (c.contract_number || "").trim());
      if (!hasAnyContract) {
        toast.error("Enter at least one Project Contract Number");
        return;
      }
    }
    setSaving(true);
    try {
      // Server-side `_normalize_project_contracts` mirrors the row marked
      // is_current into the legacy flat fields, so no client-side casts
      // are needed here — just pass `form` through.
      if (project) await api.put(`/projects/${project.id}`, form);
      else await api.post("/projects", form);
      toast.success(project ? "Project updated" : "Project created");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  return (
    <div className="bg-white -mt-6 lg:-mt-10 -mx-4 sm:-mx-6 lg:-mx-10 min-h-[calc(100vh-38px)]" data-testid="project-modal">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-[38px] bg-white z-10 shadow-sm">
          <h2 className="font-display text-xl font-bold">{project ? "Edit Project" : "New Project"}</h2>
          <button onClick={onClose} data-testid="modal-close"><X size={18} /></button>
        </div>
        <form onSubmit={save} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-[1400px] mx-auto">
          <Field label="Branch *">
            <BranchSelect value={form.branch_id} onChange={(id, name) => setForm({ ...form, branch_id: id, branch_name: name })} testid="project-branch-select" />
          </Field>
          <Field label="Project Code *"><input data-testid="field-code" required value={form.project_code} onChange={e => set("project_code", e.target.value)} className={inputCls} /></Field>
          <Field label="Project Name *"><input data-testid="field-name" required value={form.project_name} onChange={e => set("project_name", e.target.value)} className={inputCls} /></Field>
          <Field label="Client Name *">
            <input list="project-client-master-list"
                   data-testid="field-client-name"
                   required
                   value={form.client_name}
                   onChange={e => set("client_name", e.target.value)}
                   placeholder={clients.length ? `Search ${clients.length} clients…` : "Type client name"}
                   className={inputCls} />
            <datalist id="project-client-master-list">
              {clients.map(c => (
                <option key={c.id} value={c.name}>
                  {c.client_code ? `${c.client_code} · ` : ""}{c.gst_number || ""}
                </option>
              ))}
            </datalist>
          </Field>
          <Field label="Division / Location"><input value={form.division || ""} onChange={e => set("division", e.target.value)} className={inputCls} /></Field>
          <Field label="Work Type">
            <select data-testid="field-work-type" value={form.work_type}
                    onChange={e => handleWorkTypeChange(e.target.value)} className={inputCls}>
              {/* Ensure the current value is always visible even if the list
                  hasn't loaded yet or the row has a legacy type that was
                  later renamed/removed. */}
              {form.work_type && !workTypes.includes(form.work_type) && (
                <option value={form.work_type}>{form.work_type}</option>
              )}
              {workTypes.map(w => <option key={w} value={w}>{w}</option>)}
              <option value="__add_new__" data-testid="work-type-add-new">+ Add new type…</option>
            </select>
          </Field>
          <Field label="Status">
            <select value={form.status} onChange={e => set("status", e.target.value)} className={inputCls}>
              {STATUSES.map(s => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Project Group">
            {/* Sprint 2026-Q1d / Asset Depreciation Step ① — Project Group
                classifier. Drives reporting roll-ups (Revenue vs Internal
                Cost vs Corporate vs Capital). Defaults server-side to
                "Revenue Project" if left blank — see _resolve_project_group
                in server.py. Mutable from this dropdown; every change
                writes a row to the project's change_history audit log. */}
            <select
              data-testid="field-project-group"
              value={form.project_group_id || ""}
              onChange={e => {
                const id = e.target.value;
                const found = projectGroups.find(g => g.id === id);
                setForm({
                  ...form,
                  project_group_id:   id,
                  project_group_name: found?.name || "",
                });
              }}
              className={inputCls}
            >
              <option value="">— Default (Revenue Project) —</option>
              {projectGroups.map(g => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Project Manager"><input value={form.project_manager || ""} onChange={e => set("project_manager", e.target.value)} className={inputCls} /></Field>
          <Field label="Client POC"><input data-testid="field-client-poc" value={form.client_poc || ""} onChange={e => set("client_poc", e.target.value)} placeholder="Client point of contact — name / phone" className={inputCls} /></Field>
          <Field label="RO Number"><input data-testid="field-ro-number" value={form.ro_number || ""} onChange={e => set("ro_number", e.target.value)} className={inputCls} /></Field>
          <Field label="RO Approved / Not Approved">
            <select data-testid="field-ro-status" value={form.ro_status || ""} onChange={e => set("ro_status", e.target.value)} className={inputCls}>
              <option value="">— not set —</option>
              <option value="Approved">Approved</option>
              <option value="Not Approved">Not Approved</option>
            </select>
          </Field>
          <Field label="Project Start Date"><input data-testid="field-project-start" type="date" value={form.project_start_date || ""} onChange={e => set("project_start_date", e.target.value)} className={inputCls} /></Field>
          <Field label="Project End Date"><input data-testid="field-project-end" type="date" value={form.project_end_date || ""} onChange={e => set("project_end_date", e.target.value)} className={inputCls} /></Field>
          <Field label="Project Address" full><textarea data-testid="field-project-address" value={form.project_address || ""} onChange={e => set("project_address", e.target.value)} rows={2} className={inputCls} /></Field>
          <Field label="Notes" full><textarea value={form.notes || ""} onChange={e => set("notes", e.target.value)} rows={3} className={inputCls} /></Field>

          {/* ── Sprint 2026-Q1e · Phase 3 · Authorized Users multi-select ──
              Restricts who can VIEW, CREATE, EDIT and APPROVE transactions
              against this project. Empty list = same-branch access
              (legacy grandfather behaviour preserved). Admin and users
              with `view_all_projects.view` bypass this filter entirely. */}
          <div className="md:col-span-2" data-testid="project-authorized-users-card">
            <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1.5">
              Authorized Users
              <span className="text-slate-400 normal-case font-normal ml-2">
                — leave empty to allow every user in the selected branch
              </span>
            </label>
            {!form.branch_id ? (
              <div className="border border-slate-300 rounded px-3 py-2.5 text-xs text-slate-500 bg-slate-50">
                Pick a branch first to see eligible users.
              </div>
            ) : authCandidatesLoading ? (
              <div className="border border-slate-300 rounded px-3 py-2.5 text-xs text-slate-500">
                Loading candidates…
              </div>
            ) : authCandidates.length === 0 ? (
              <div className="border border-slate-300 rounded px-3 py-2.5 text-xs text-slate-500 bg-slate-50">
                No active users have branch scope covering this branch.
                An administrator must first add branch scope on the User Master.
              </div>
            ) : (
              <div data-testid="authorized-users-container">
                {/* Sprint 2026-Feb-10i · Search + bulk-select for the
                    Authorized Users list. Growing tenants have 30-60+
                    users per branch → un-searchable checkbox list
                    forces the user to scroll and eyeball. Search
                    filters on name / email / role (case-insensitive);
                    the ✓ Select all / ✗ Clear actions apply to the
                    CURRENTLY-FILTERED subset so an admin can quickly
                    grant access to "everyone with role X" in two
                    clicks. */}
                {(() => {
                  const q = (authUserSearch || "").trim().toLowerCase();
                  const filtered = q
                    ? authCandidates.filter(u => (
                          (u.name  || "").toLowerCase().includes(q)
                       || (u.email || "").toLowerCase().includes(q)
                       || (u.role  || "").toLowerCase().includes(q)
                      ))
                    : authCandidates;
                  const filteredIds = filtered.map(u => u.id);
                  const selected   = new Set(form.authorized_users || []);
                  const allSelectedInFilter = filteredIds.length > 0 && filteredIds.every(id => selected.has(id));
                  return (
                    <>
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="relative flex-1">
                          <input
                            type="text"
                            value={authUserSearch}
                            onChange={e => setAuthUserSearch(e.target.value)}
                            placeholder="Search by name, email or role…"
                            data-testid="authorized-users-search"
                            className="w-full text-xs border border-slate-300 rounded pl-7 pr-2 py-1.5 focus:border-orange-500 focus:outline-none"
                          />
                          <svg className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <circle cx="11" cy="11" r="7" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                          </svg>
                          {authUserSearch && (
                            <button
                              type="button"
                              onClick={() => setAuthUserSearch("")}
                              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 text-xs"
                              data-testid="authorized-users-search-clear"
                              aria-label="Clear search"
                            >×</button>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setForm(f => {
                              const cur = new Set(f.authorized_users || []);
                              if (allSelectedInFilter) {
                                filteredIds.forEach(id => cur.delete(id));
                              } else {
                                filteredIds.forEach(id => cur.add(id));
                              }
                              return { ...f, authorized_users: [...cur] };
                            });
                          }}
                          disabled={filteredIds.length === 0}
                          className="text-[10px] font-bold uppercase tracking-wider text-orange-700 hover:text-orange-900 disabled:opacity-40 whitespace-nowrap"
                          data-testid="authorized-users-select-all"
                        >
                          {allSelectedInFilter ? "✗ Clear all" : "✓ Select all"}
                          {q && <span className="ml-1 text-slate-400 font-normal normal-case">({filteredIds.length})</span>}
                        </button>
                      </div>
                      <div
                        data-testid="authorized-users-list"
                        className="border border-slate-300 rounded max-h-56 overflow-y-auto divide-y divide-slate-100 bg-white"
                      >
                        {filtered.length === 0 ? (
                          <div className="px-3 py-4 text-center text-[11px] text-slate-400">
                            No users match &ldquo;<span className="font-mono">{authUserSearch}</span>&rdquo;. Clear search to see all {authCandidates.length} candidates.
                          </div>
                        ) : filtered.map(u => {
                          const checked = selected.has(u.id);
                          return (
                            <label
                              key={u.id}
                              className="flex items-center gap-3 px-3 py-2 text-xs hover:bg-slate-50 cursor-pointer"
                              data-testid={`authorized-user-row-${u.id}`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                data-testid={`authorized-user-checkbox-${u.id}`}
                                onChange={e => {
                                  setForm(f => {
                                    const cur = new Set(f.authorized_users || []);
                                    if (e.target.checked) cur.add(u.id);
                                    else cur.delete(u.id);
                                    return { ...f, authorized_users: [...cur] };
                                  });
                                }}
                              />
                              <span className="flex-1">
                                <span className="font-medium text-slate-900">{u.name || u.email}</span>
                                {u.role && <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500">{u.role}</span>}
                              </span>
                              <span className="text-slate-400">{u.email}</span>
                            </label>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
            {(form.authorized_users || []).length > 0 && (
              <div className="mt-1.5 text-[11px] text-slate-500" data-testid="authorized-users-count">
                {form.authorized_users.length} user(s) selected · they will be the ONLY ones who can view, transact and approve on this project (plus admins and view-all users).
              </div>
            )}
          </div>

          {/* ── Sprint 2026-Q1 · Admin-controlled Per-Project Budget Lock ──
              Hard-blocks any new Purchase Invoice / Store Rent / Salary /
              General Payment whose amount would push this project's
              consumption past the approved effective budget. Visible to
              everyone for transparency but editable ONLY by Admins. Has
              no effect when no budget rows are Approved for the project
              (no cap defined → nothing to enforce). */}
          <div className="md:col-span-2" data-testid="project-budget-lock-card">
            <div className={`border rounded-md p-4 flex items-start gap-3 ${form.enforce_budget_lock ? "border-amber-400 bg-amber-50" : "border-slate-300 bg-slate-50"}`}>
              <Lock size={18} className={form.enforce_budget_lock ? "text-amber-600 mt-0.5" : "text-slate-400 mt-0.5"} />
              <div className="flex-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="project-enforce-budget-lock"
                    checked={!!form.enforce_budget_lock}
                    onChange={e => set("enforce_budget_lock", e.target.checked)}
                    disabled={!isAdmin}
                    className="h-4 w-4 cursor-pointer disabled:cursor-not-allowed"
                  />
                  <span className="text-sm font-bold text-slate-800">
                    Lock Purchase Vouchers if Consumption exceeds Budget
                  </span>
                  {!isAdmin && (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-200 px-2 py-0.5 rounded">
                      Admin only
                    </span>
                  )}
                </label>
                <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">
                  When enabled, the system will refuse any new Purchase Invoice, Store Rent Payment,
                  Salary Payment or General Payment that would push this project&apos;s total consumption
                  past the approved effective budget. Has no effect until at least one budget category
                  is approved for this project.
                  {!isAdmin && (
                    <span className="block mt-1 text-slate-500">
                      Only an Administrator can change this setting.
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* ── Project Contracts editor ─────────────────────────────────
              The four legacy fields (Project Contract Number / Start Date
              / End Date / Project Value) have been moved into this multi-
              row editor so the user can capture every quarterly /
              yearly contract renewal under the same Project Code. The
              row marked CURRENT mirrors its values into the legacy flat
              fields on the server so every report / PDF / voucher
              already-reading them keeps working unchanged. */}
          <div className="md:col-span-2 mt-2" data-testid="project-contracts-editor">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                Project Contracts <span className="text-slate-400 normal-case font-normal">— pick one as Current Running</span>
              </label>
              <button type="button"
                      data-testid="add-contract-btn"
                      onClick={addContract}
                      className="text-[11px] font-bold uppercase tracking-wider text-[#F97316] hover:underline flex items-center gap-1">
                <Plus size={12} /> Add Contract
              </button>
            </div>
            <div className="border border-slate-300 overflow-x-auto">
              <table className="w-full min-w-[640px] text-xs">
                <thead className="bg-slate-50 text-slate-600 uppercase text-[10px] tracking-wider">
                  <tr>
                    <th className="py-2 px-2 text-center w-16">Current</th>
                    <th className="py-2 px-2 text-left">Contract No *</th>
                    <th className="py-2 px-2 text-left w-36">Start Date</th>
                    <th className="py-2 px-2 text-left w-36">End Date</th>
                    <th className="py-2 px-2 text-right w-40">Project Value (INR)</th>
                    <th className="py-2 px-2 text-left">Remarks</th>
                    <th className="py-2 px-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.contracts.map((c, i) => (
                    <Fragment key={i}>
                    <tr className={`border-t border-slate-200 ${c.is_current ? "bg-orange-50/50" : ""}`} data-testid={`project-contract-row-${i}`}>
                      <td className="py-1.5 px-2 text-center">
                        <input type="radio" name="current-contract" checked={!!c.is_current}
                               onChange={() => markContractCurrent(i)}
                               data-testid={`project-contract-row-${i}-mark-current`}
                               aria-label={`Mark contract row ${i + 1} as current`} />
                      </td>
                      <td className="py-1.5 px-2">
                        <input value={c.contract_number || ""}
                               onChange={e => updateContract(i, { contract_number: e.target.value })}
                               placeholder="e.g. RCPL-CONTR-2026-Q1-0042"
                               data-testid={`project-contract-row-${i}-no`}
                               className="w-full border border-slate-300 px-2 py-1 text-xs focus:outline-none focus:border-[#F97316]" />
                      </td>
                      <td className="py-1.5 px-2">
                        <input type="date" value={c.start_date || ""}
                               onChange={e => updateContract(i, { start_date: e.target.value })}
                               data-testid={`project-contract-row-${i}-start`}
                               className="w-full border border-slate-300 px-2 py-1 text-xs focus:outline-none focus:border-[#F97316]" />
                      </td>
                      <td className="py-1.5 px-2">
                        <input type="date" value={c.end_date || ""}
                               onChange={e => updateContract(i, { end_date: e.target.value })}
                               data-testid={`project-contract-row-${i}-end`}
                               className="w-full border border-slate-300 px-2 py-1 text-xs focus:outline-none focus:border-[#F97316]" />
                      </td>
                      <td className="py-1.5 px-2">
                        <input type="number" step="0.01" value={c.project_value || 0}
                               onChange={e => updateContract(i, { project_value: e.target.value })}
                               data-testid={`project-contract-row-${i}-value`}
                               className="w-full border border-slate-300 px-2 py-1 text-xs text-right tabular focus:outline-none focus:border-[#F97316]" />
                      </td>
                      <td className="py-1.5 px-2">
                        <input value={c.remarks || ""}
                               onChange={e => updateContract(i, { remarks: e.target.value })}
                               placeholder="Optional"
                               data-testid={`project-contract-row-${i}-remarks`}
                               className="w-full border border-slate-300 px-2 py-1 text-xs focus:outline-none focus:border-[#F97316]" />
                      </td>
                      <td className="py-1.5 px-2 text-center">
                        {form.contracts.length > 1 && (
                          <button type="button"
                                  onClick={() => removeContract(i)}
                                  data-testid={`project-contract-row-${i}-delete`}
                                  className="text-red-600 hover:text-red-800"
                                  aria-label={`Delete contract row ${i + 1}`}>
                            <X size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                    {/* BG sub-row — captures the contract's Bank Guarantee
                        details. Hidden by default; the BG Created checkbox
                        is always visible so the operator can toggle it on
                        with one click. When ON, the row expands to show
                        the full BG capture fields. */}
                    <tr key={`bg-${i}`}
                        className={`border-t border-dashed border-slate-200 bg-slate-50/40 ${c.is_current ? "bg-orange-50/30" : ""}`}
                        data-testid={`project-contract-row-${i}-bg-block`}>
                      <td colSpan={7} className="py-2 px-2">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[11px]">
                          <label className="flex items-center gap-1.5 font-bold uppercase tracking-wider text-slate-600 whitespace-nowrap">
                            <input type="checkbox"
                                   checked={!!c.bg_created}
                                   onChange={e => updateContract(i, { bg_created: e.target.checked })}
                                   data-testid={`project-contract-row-${i}-bg-created`} />
                            BG Created
                          </label>
                          {c.bg_created ? (
                            <>
                              <label className="flex items-center gap-1 text-slate-600">
                                BG Bank
                                <input value={c.bg_bank_name || ""}
                                       onChange={e => updateContract(i, { bg_bank_name: e.target.value })}
                                       placeholder="e.g. SBI"
                                       data-testid={`project-contract-row-${i}-bg-bank-name`}
                                       className="border border-slate-300 px-2 py-1 text-[11px] w-32 focus:outline-none focus:border-[#F97316]" />
                              </label>
                              <label className="flex items-center gap-1 text-slate-600">
                                BG No
                                <input value={c.bg_number || ""}
                                       onChange={e => updateContract(i, { bg_number: e.target.value })}
                                       placeholder="Instrument no."
                                       data-testid={`project-contract-row-${i}-bg-number`}
                                       className="border border-slate-300 px-2 py-1 text-[11px] w-36 focus:outline-none focus:border-[#F97316]" />
                              </label>
                              <label className="flex items-center gap-1 text-slate-600">
                                BG Amount ₹
                                <input type="number" step="0.01"
                                       value={c.bg_amount || 0}
                                       onChange={e => updateContract(i, { bg_amount: e.target.value })}
                                       data-testid={`project-contract-row-${i}-bg-amount`}
                                       className="border border-slate-300 px-2 py-1 text-[11px] w-28 text-right tabular focus:outline-none focus:border-[#F97316]" />
                              </label>
                              <label className="flex items-center gap-1 text-slate-600">
                                BG Expiry
                                <input type="date" value={c.bg_expiry_date || ""}
                                       onChange={e => updateContract(i, { bg_expiry_date: e.target.value })}
                                       data-testid={`project-contract-row-${i}-bg-expiry`}
                                       className="border border-slate-300 px-2 py-1 text-[11px] focus:outline-none focus:border-[#F97316]" />
                              </label>
                              <label className="flex items-center gap-1 text-slate-600">
                                BG Claim Expiry
                                <input type="date" value={c.bg_claim_expiry_date || ""}
                                       onChange={e => updateContract(i, { bg_claim_expiry_date: e.target.value })}
                                       data-testid={`project-contract-row-${i}-bg-claim-expiry`}
                                       className="border border-slate-300 px-2 py-1 text-[11px] focus:outline-none focus:border-[#F97316]" />
                              </label>
                              <label className="flex items-center gap-1 text-slate-600">
                                BG FD No
                                <input value={c.bg_fd_number || ""}
                                       onChange={e => updateContract(i, { bg_fd_number: e.target.value })}
                                       placeholder="FD reference"
                                       data-testid={`project-contract-row-${i}-bg-fd-number`}
                                       className="border border-slate-300 px-2 py-1 text-[11px] w-32 focus:outline-none focus:border-[#F97316]" />
                              </label>
                            </>
                          ) : (
                            <span className="text-[10px] text-slate-400 italic">Tick BG Created to capture Bank Guarantee details for this contract</span>
                          )}
                        </div>
                      </td>
                    </tr>
                    </Fragment>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wider border-t border-slate-300">
                    <td colSpan={4} className="py-2 px-2 text-right">Total Across Contracts</td>
                    <td className="py-2 px-2 text-right font-bold tabular text-slate-900" data-testid="project-contracts-total">
                      {formatINR(form.contracts.reduce((s, c) => s + (Number(c.project_value) || 0), 0))}
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
          <div className="md:col-span-2 flex justify-end gap-2 pt-2 border-t border-slate-200">
            <button type="button" onClick={onClose} className="px-5 py-2.5 border border-slate-300 text-sm font-bold uppercase tracking-wider">Cancel</button>
            <button data-testid="save-project-btn" disabled={saving} type="submit" className="px-5 py-2.5 bg-[#F97316] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#EA580C] disabled:opacity-60">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// ProjectCostSummaryTab — Sprint 1
// ═══════════════════════════════════════════════════════════════════════
// Per-project version of the dashboard cost-control card. Pulls the same
// `cost-summary` endpoint and renders:
//   • 5-KPI strip (Budgeted · Committed · Consumed · P&L · Variance)
//   • Cost-by-category breakdown (Direct Material / Labour / Subcontractor / ...)
//   • Open commitments + Assets-assigned visibility section
//   • Deep-link to the Project Cost Event Ledger report for this project
//
// Exported so other pages (e.g. ProjectHealthDashboardPage) can deep-link
// into the same cost-summary view via `useFormTab`.
export function ProjectCostSummaryTab({ project, onClose }) {
  const [s, setS] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get(`/projects/${project.id}/cost-summary`),
      api.get(`/projects/${project.id}/budget-health`),
    ])
      .then(([r1, r2]) => {
        if (cancelled) return;
        setS(r1.data);
        setHealth(r2.data);
      })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.detail || "Could not load cost summary"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [project.id]);

  if (loading) {
    return <div className="text-center text-slate-500 py-12">Loading cost summary…</div>;
  }
  if (err) {
    return <div className="text-center text-rose-600 py-12">{err}</div>;
  }
  if (!s) {
    return <div className="text-center text-slate-400 py-12">No cost summary available.</div>;
  }

  const profitPositive = s.profit_loss >= 0;
  const categoryRows = Object.entries(s.actual_by_category || {})
    .sort((a, b) => b[1] - a[1]);

  return (
    <div className="space-y-6" data-testid="project-cost-summary-tab">
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow={`Project · ${s.project_code || ""}`}
        title={`Cost Summary — ${s.project_name || ""}`}
        subtitle={`${s.client_name || ""} · ${s.branch_name || ""} · FY ${s.fy_code || "Current"}`}
        actions={
          <button
            type="button"
            onClick={onClose}
            className="text-xs uppercase tracking-wider text-slate-500 hover:text-slate-900"
            data-testid="cost-summary-close-btn"
          >
            <X size={16} className="inline mr-1" /> Close
          </button>
        }
      />

      {/* Sprint 3 — Budget Health Card (status badge + 7 spec fields) */}
      {health && <BudgetHealthCard health={health} />}

      {/* 6-tile strip — Budget · Committed · Consumed · Revenue · Current P&L · Expected P&L */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-slate-100 rounded-xl border border-slate-200 overflow-hidden">
        <CSTile label="Budget" value={s.budgeted} tone="slate"
                notConfigured={!s.has_budget}
                hint={s.has_budget && s.variance_pct != null
                       ? `${s.variance_pct.toFixed(1)}% remaining`
                       : "Configure budget →"}
                testid="cs-tile-budgeted" />
        <CSTile label="Committed" value={s.committed} tone="amber"
                hint={`Open: ${formatINR(s.open_commitments || 0)}`}
                testid="cs-tile-committed" />
        <CSTile label="Consumed" value={s.consumed} tone="rose"
                hint="Hit P&L" testid="cs-tile-consumed" />
        <CSTile label="Revenue" value={s.revenue_billed} tone="slate"
                hint="Approved SI − CN" testid="cs-tile-revenue" />
        <CSTile label="Current P&L"
                value={s.profit_loss}
                tone={profitPositive ? "emerald" : "rose"}
                hint="Revenue − Consumed"
                testid="cs-tile-pnl" sign />
        <CSTile label="Expected P&L"
                value={s.expected_profit ?? 0}
                tone={(s.expected_profit ?? 0) >= 0 ? "emerald" : "rose"}
                hint="Rev − (Cons + Open)"
                testid="cs-tile-expected-pnl" sign />
      </div>

      {/* Breakdown by cost category */}
      <Card>
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
            Consumed Cost — By Category
          </div>
          <div className="font-display text-base font-black text-slate-900 mt-0.5">
            Where the project&apos;s actual rupees went
          </div>
        </div>
        {categoryRows.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400">
            No P&amp;L-hit cost events yet. Approve a Material Issue Voucher,
            Service PI, or Vendor Bill (Store Rent / Salary / General Payment)
            tagged to this project to start the ledger.
          </div>
        ) : (
          <div className="overflow-x-auto"><table className="w-full min-w-[620px] text-sm" data-testid="cost-by-category-table">
            <thead>
              <tr className="text-left bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-600 font-bold">
                <th className="px-5 py-2">Category</th>
                <th className="px-5 py-2 text-right">Amount (₹)</th>
                <th className="px-5 py-2 text-right">% of Total</th>
                <th className="px-5 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {categoryRows.map(([cat, amt]) => {
                const pct = s.consumed > 0 ? (amt / s.consumed) * 100 : 0;
                return (
                  <tr key={cat} className="border-b border-slate-100"
                      data-testid={`cost-cat-${cat.toLowerCase().replace(/\s+/g, "-")}`}>
                    <td className="px-5 py-2 font-medium text-slate-800">{cat}</td>
                    <td className="px-5 py-2 text-right tabular-nums">{formatINR(amt)}</td>
                    <td className="px-5 py-2 text-right text-xs text-slate-500">{pct.toFixed(1)}%</td>
                    <td className="px-5 py-2">
                      <div className="w-full bg-slate-100 h-1.5 rounded">
                        <div className="bg-[#F97316] h-full rounded"
                             style={{ width: `${Math.min(100, pct)}%` }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
              <tr className="bg-slate-50 font-bold border-t-2 border-slate-300">
                <td className="px-5 py-2 text-[10px] uppercase tracking-wider">Total Consumed</td>
                <td className="px-5 py-2 text-right tabular-nums text-rose-700">{formatINR(s.consumed)}</td>
                <td className="px-5 py-2 text-right text-xs">100%</td>
                <td></td>
              </tr>
            </tbody>
          </table></div>
        )}
      </Card>

      {/* Net Commitment breakdown — Sprint 2.1 (cross-project transfer aware) */}
      {(s.transfer_out > 0 || s.transfer_in > 0) && (
        <Card>
          <div className="px-5 py-4 border-b border-slate-100">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
              Net Commitment · Cross-Project Transfer Movement
            </div>
            <div className="font-display text-base font-black text-slate-900 mt-0.5">
              How much of this project&apos;s exposure is real, after transfers
            </div>
          </div>
          <table className="w-full text-sm" data-testid="net-commitment-table">
            <tbody>
              <tr className="border-b border-slate-100">
                <td className="px-5 py-2.5 font-medium text-slate-800">Original Commitment</td>
                <td className="px-5 py-2.5 text-right tabular-nums">{formatINR(s.original_commitment || 0)}</td>
                <td className="px-5 py-2.5 text-[10px] text-slate-400">Σ PI_Committed events</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="px-5 py-2.5 font-medium text-rose-700">
                  <span className="inline-block w-3 text-center">−</span> Transfer Out
                </td>
                <td className="px-5 py-2.5 text-right tabular-nums text-rose-700">{formatINR(s.transfer_out || 0)}</td>
                <td className="px-5 py-2.5 text-[10px] text-slate-400">Material issued to other projects</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="px-5 py-2.5 font-medium text-emerald-700">
                  <span className="inline-block w-3 text-center">+</span> Transfer In
                </td>
                <td className="px-5 py-2.5 text-right tabular-nums text-emerald-700">{formatINR(s.transfer_in || 0)}</td>
                <td className="px-5 py-2.5 text-[10px] text-slate-400">Material received from other projects</td>
              </tr>
              <tr className="bg-slate-50 font-bold border-t-2 border-slate-300">
                <td className="px-5 py-3 text-[10px] uppercase tracking-wider">Net Commitment</td>
                <td className="px-5 py-3 text-right tabular-nums text-slate-900">{formatINR(s.net_commitment || 0)}</td>
                <td className="px-5 py-3 text-[10px] text-slate-400">Original − Out + In</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="px-5 py-2.5 font-medium text-slate-800">Less: Consumed (on own batches)</td>
                <td className="px-5 py-2.5 text-right tabular-nums text-rose-700">{formatINR(s.consumed || 0)}</td>
                <td className="px-5 py-2.5 text-[10px] text-slate-400">Hits this project&apos;s P&amp;L</td>
              </tr>
              <tr className="bg-amber-50 font-bold">
                <td className="px-5 py-3 text-[10px] uppercase tracking-wider">Open Exposure</td>
                <td className="px-5 py-3 text-right tabular-nums text-amber-700">{formatINR(s.open_commitments || 0)}</td>
                <td className="px-5 py-3 text-[10px] text-slate-400">What&apos;s still on the books</td>
              </tr>
            </tbody>
          </table>
        </Card>
      )}

      {/* Side strip — Open Commitments + Assets visibility */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <div className="px-5 py-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
              Open Commitments
            </div>
            <div className="font-display text-2xl font-black text-amber-700 mt-1 tabular-nums">
              {formatINR(s.open_commitments || 0)}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              PI signed but materials not yet issued (Committed − Consumed)
            </div>
            <div className="mt-3 text-xs text-slate-600">
              Forecast cost at close-out:
              <span className="font-bold ml-2 tabular-nums">{formatINR(s.forecast || 0)}</span>
              {s.budgeted > 0 && (
                <span className="ml-2 text-[10px] text-slate-400">
                  ({s.forecast > s.budgeted ? "over" : "within"} budget)
                </span>
              )}
            </div>
          </div>
        </Card>
        <Card>
          <div className="px-5 py-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
              Assets Assigned
            </div>
            <div className="font-display text-2xl font-black text-slate-800 mt-1 tabular-nums">
              {s.assets_assigned?.count || 0}
              <span className="text-xs text-slate-400 ml-2 font-normal">
                {formatINR(s.assets_assigned?.capitalised_value || 0)} capitalised
              </span>
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Visibility only — assets capitalise, not expensed against this project.
            </div>
          </div>
        </Card>
      </div>

      {/* Deep-link to full event ledger */}
      <Card>
        <div className="px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
              Drill Down
            </div>
            <div className="text-sm text-slate-700 mt-0.5">
              See every cost event posted against this project — PI Committed,
              Material Consumed, Direct Expense, Reversals.
            </div>
          </div>
          <button
            type="button"
            onClick={() => downloadReport("project-cost-event-ledger",
              { project_id: project.id, from_date: "2026-04-01", to_date: "2027-03-31" })}
            className="bg-[#0B1F3A] text-white text-xs uppercase tracking-wider font-bold px-4 py-2 rounded hover:bg-slate-700"
            data-testid="cost-summary-export-ledger"
          >
            <FileSpreadsheet size={14} className="inline mr-2 -mt-0.5" />
            Export Event Ledger
          </button>
        </div>
      </Card>
    </div>
  );
}

function CSTile({ label, value, hint, tone = "slate", testid, sign, notConfigured }) {
  const palette = {
    slate:   "text-slate-700",
    amber:   "text-amber-700",
    rose:    "text-rose-700",
    emerald: "text-emerald-700",
  }[tone] || "text-slate-700";
  let display;
  if (notConfigured) {
    display = "Not Configured";
  } else if (sign && value >= 0) {
    display = `+${formatINR(value)}`;
  } else {
    display = formatINR(value);
  }
  return (
    <div className="bg-white px-4 py-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">
        {label}
      </div>
      <div className={`font-display ${notConfigured ? "text-sm italic" : "text-xl"} font-black tabular-nums ${notConfigured ? "text-slate-400" : palette}`}>
        {display}
      </div>
      {hint && (
        <div className="text-[10px] text-slate-400 mt-0.5 truncate" title={hint}>
          {hint}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// BudgetHealthCard — Sprint 3 (Budget Breach Alerts)
// ═══════════════════════════════════════════════════════════════════════
// Renders the status badge (Green / Amber / Red / Breach / Not Configured)
// plus the 7 spec fields. Bands per the product spec:
//   util  < 80%        → Healthy   (green)
//   80% ≤ util < 90%   → Warning   (amber)
//   90% ≤ util ≤ 100%  → Critical  (red)
//   util  > 100%       → Breach    (red bold)
//   no budget          → Not_Configured (neutral grey, no alert)
const HEALTH_TONES = {
  Healthy:        { bg: "bg-emerald-50", text: "text-emerald-800", ring: "ring-emerald-200", label: "Healthy",      hint: "Within 80% of Budget" },
  Warning:        { bg: "bg-amber-50",   text: "text-amber-800",   ring: "ring-amber-200",   label: "Warning",      hint: "Reached 80% of Budget" },
  Critical:       { bg: "bg-rose-50",    text: "text-rose-800",    ring: "ring-rose-200",    label: "Critical",     hint: "Reached 90% of Budget" },
  Breach:         { bg: "bg-rose-100",   text: "text-rose-900",    ring: "ring-rose-300",    label: "Budget Breach", hint: "Exceeded 100% of Budget" },
  Not_Configured: { bg: "bg-slate-50",   text: "text-slate-600",   ring: "ring-slate-200",   label: "Budget Not Configured", hint: "Configure a budget to enable alerts" },
};

function BudgetHealthCard({ health }) {
  const tone = HEALTH_TONES[health.status] || HEALTH_TONES.Not_Configured;
  const util = health.utilization_pct;
  const utilStr = (util == null) ? "—" : `${util.toFixed(1)}%`;
  return (
    <Card className={`rounded-xl ring-1 ${tone.ring}`}>
      <div className={`${tone.bg} px-5 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3`}
           data-testid="budget-health-card">
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] ${tone.text} bg-white/70 rounded-full`}
                data-testid="budget-health-status">
            {tone.label}
          </span>
          <span className="text-[11px] text-slate-600 font-bold">
            Utilization · <span className="tabular-nums" data-testid="budget-health-utilization">{utilStr}</span>
          </span>
          <span className="text-[10px] text-slate-500">{tone.hint}</span>
        </div>
        {(health.variance != null) && (
          <span className={`text-[11px] tabular-nums font-black ${health.variance < 0 ? "text-rose-800" : "text-emerald-800"}`}
                data-testid="budget-health-variance-pill">
            {health.variance < 0 ? "Over Budget by " : "Remaining "}
            {formatINR(Math.abs(health.variance))}
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-100">
        <HCell label="Budget"           value={health.budget}           none={!health.has_budget} testid="bh-budget" />
        <HCell label="Consumed"         value={health.consumed}                                   testid="bh-consumed" />
        <HCell label="Open Commitments" value={health.open_commitments}                            testid="bh-open" />
        <HCell label="Variance"         value={health.variance}         none={health.variance == null}
               signed                                                                              testid="bh-variance" />
        <HCell label="Current Profit"   value={health.current_profit}   signed                     testid="bh-current-profit" />
        <HCell label="Expected Profit"  value={health.expected_profit}  signed                     testid="bh-expected-profit" />
        <HCell label="Revenue Billed"   value={health.revenue_billed}                              testid="bh-revenue" />
        <HCell label="Project"          text={health.project_name}                                 testid="bh-project-name" />
      </div>
    </Card>
  );
}

function HCell({ label, value, text, signed, none, testid }) {
  let display;
  if (text != null) {
    display = text || "—";
  } else if (none) {
    display = "—";
  } else if (signed && value >= 0) {
    display = `+${formatINR(value)}`;
  } else {
    display = formatINR(value || 0);
  }
  const valueClass = signed && value != null
    ? (value < 0 ? "text-rose-700" : "text-emerald-700")
    : "text-slate-900";
  return (
    <div className="bg-white px-4 py-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
      <div className={`font-display ${text != null ? "text-sm" : "text-base"} font-black tabular-nums mt-0.5 ${none ? "text-slate-400 italic" : valueClass}`}>
        {display}
      </div>
    </div>
  );
}




export const inputCls = "w-full border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-[#F97316] transition-colors";
export function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1.5">{label}</label>
      {children}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// ProjectBudgetTab — Sprint 2
// ═══════════════════════════════════════════════════════════════════════
// Per-project Budget Maintenance grid:
//   • 8 canonical cost-category rows (Material, Labour, Subcontractor,
//     Vehicle Hire, Site Rent, Salaries, General Expenses, Other)
//   • Per-row inline edit of Revised Amount + Revision Remarks
//   • Per-row Revision History drawer
//   • Bulk Save → POSTs the whole grid → submits to Approval Matrix
//
// Sprint 2 decision recap:
//   • Original Amount frozen at first approval.
//   • Every revision needs fresh approval (re-uses matrix engine).
//   • Append-only revision_history per row.
//   • "Not Configured" status when a row was never saved.
const BUDGET_CATEGORIES = [
  "Material", "Labour", "Subcontractor", "Vehicle Hire",
  "Site Rent", "Salaries", "General Expenses", "Other",
];

function ProjectBudgetTab({ project, onClose }) {
  const [fyCode, setFyCode] = useState("26-27");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({});            // {cat: amount}
  const [draftRemarks, setDraftRemarks] = useState({}); // {cat: remarks}
  const [historyFor, setHistoryFor] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/projects/${project.id}/budget?fy_code=${fyCode}`)
      .then((r) => {
        setData(r.data);
        // seed draft state from current revised_amount values
        const d = {};
        r.data.rows.forEach((row) => {
          d[row.cost_category] = row.revised_amount || row.original_amount || 0;
        });
        setDraft(d);
        setDraftRemarks({});
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load budget"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [project.id, fyCode]);

  const handleSave = async () => {
    // Build rows that have a non-zero amount OR an existing row in the
    // grid (so user can set a row to 0 deliberately).
    const rows = BUDGET_CATEGORIES
      .filter((cat) => {
        const existing = data?.rows.find((r) => r.cost_category === cat);
        const isNew = !existing || existing.status === "NotConfigured";
        const amt = Number(draft[cat]) || 0;
        // Skip new categories left at 0; allow existing categories to be revised to anything
        return !isNew || amt > 0;
      })
      .map((cat) => ({
        cost_category: cat,
        amount: Number(draft[cat]) || 0,
        revision_remarks: draftRemarks[cat] || "",
      }));
    if (rows.length === 0) {
      toast.info("Enter at least one budget amount before saving.");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post(`/projects/${project.id}/budget`, {
        fy_code: fyCode, rows,
      });
      if (r.data.no_changes) {
        toast.info("No changes detected. Nothing to save.");
      } else {
        const status = r.data.approval_status || "Pending";
        toast.success(`Saved ${r.data.saved_count} row(s). Approval status: ${status}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const showHistory = async (row) => {
    try {
      const r = await api.get(`/projects/${project.id}/budget/${row.id}/history`);
      setHistoryFor(r.data);
    } catch (e) {
      toast.error("Could not load revision history");
    }
  };

  if (loading || !data) {
    return <div className="text-center text-slate-500 py-12">Loading budget…</div>;
  }

  const totalDraft = BUDGET_CATEGORIES.reduce((s, c) => s + (Number(draft[c]) || 0), 0);
  const totalApproved = data.totals.effective;
  const draftChanged = Math.abs(totalDraft - totalApproved) > 0.5
    || BUDGET_CATEGORIES.some((c) => (draftRemarks[c] || "").trim());

  return (
    <div className="space-y-6" data-testid="project-budget-tab">
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow={`Project · ${data.project_code || ""}`}
        title={`Budget — ${data.project_name || ""}`}
        subtitle={`${data.client_name || ""} · FY ${data.fy_code || "—"}`}
        actions={
          <div className="flex items-center gap-3">
            <select
              value={fyCode}
              onChange={(e) => setFyCode(e.target.value)}
              className="border border-slate-300 px-3 py-1.5 text-xs font-bold uppercase tracking-wider"
              data-testid="budget-fy-select"
            >
              {["25-26", "26-27", "27-28", "28-29"].map((fy) => (
                <option key={fy} value={fy}>{`FY ${fy}`}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={onClose}
              className="text-xs uppercase tracking-wider text-slate-500 hover:text-slate-900"
              data-testid="budget-close-btn"
            >
              <X size={16} className="inline mr-1" /> Close
            </button>
          </div>
        }
      />

      {/* Grid — 8 canonical category rows */}
      <Card>
        <div className="overflow-x-auto"><table className="w-full min-w-[720px] text-sm" data-testid="budget-grid-table">
          <thead>
            <tr className="text-left bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-600 font-bold">
              <th className="px-5 py-3">Category</th>
              <th className="px-5 py-3 text-right">Original (₹)</th>
              <th className="px-5 py-3 text-right">Revised (₹)</th>
              <th className="px-5 py-3 text-right">% Δ</th>
              <th className="px-5 py-3">Revision Remarks</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => {
              const cat = row.cost_category;
              const orig = row.original_amount || 0;
              const drafted = Number(draft[cat] ?? 0);
              const pctDelta = orig > 0 ? ((drafted - orig) / orig * 100).toFixed(1) : "—";
              const pctIsPositive = drafted >= orig;
              return (
                <tr key={cat} className="border-b border-slate-100"
                    data-testid={`budget-row-${cat.toLowerCase().replace(/\s+/g, "-")}`}>
                  <td className="px-5 py-2.5 font-bold text-slate-900">{cat}</td>
                  <td className="px-5 py-2.5 text-right tabular-nums text-slate-600">
                    {orig > 0 ? formatINR(orig) : <span className="text-slate-300">—</span>}
                    {row.locked && (
                      <Lock size={11} className="inline ml-1.5 text-emerald-600 -mt-0.5" />
                    )}
                  </td>
                  <td className="px-5 py-2.5 text-right">
                    <input
                      type="number"
                      value={draft[cat] ?? ""}
                      onChange={(e) =>
                        setDraft({ ...draft, [cat]: e.target.value })}
                      className="w-32 border border-slate-300 px-2 py-1 text-right text-sm focus:border-[#F97316] focus:outline-none tabular-nums"
                      placeholder="0"
                      data-testid={`budget-amt-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                    />
                  </td>
                  <td className={`px-5 py-2.5 text-right text-xs font-bold tabular-nums ${
                      pctDelta === "—" ? "text-slate-400"
                      : pctIsPositive ? "text-rose-600" : "text-emerald-700"}`}>
                    {pctDelta === "—" ? "—"
                       : `${pctIsPositive ? "+" : ""}${pctDelta}%`}
                  </td>
                  <td className="px-5 py-2.5">
                    <input
                      type="text"
                      value={draftRemarks[cat] ?? ""}
                      onChange={(e) =>
                        setDraftRemarks({ ...draftRemarks, [cat]: e.target.value })}
                      className="w-full border border-slate-300 px-2 py-1 text-sm focus:border-[#F97316] focus:outline-none"
                      placeholder={row.revision_remarks || "Optional justification"}
                      data-testid={`budget-remarks-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                    />
                  </td>
                  <td className="px-5 py-2.5">
                    <BudgetStatusBadge status={row.status} revision={row.revision_no} />
                  </td>
                  <td className="px-5 py-2.5">
                    {row.id && row.status !== "NotConfigured" && (
                      <button
                        type="button"
                        onClick={() => showHistory(row)}
                        className="text-xs uppercase tracking-wider font-bold text-[#0B1F3A] hover:underline"
                        data-testid={`budget-history-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <History size={11} className="inline mr-1 -mt-0.5" /> History
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {/* Totals row */}
            <tr className="bg-slate-50 font-bold border-t-2 border-slate-300">
              <td className="px-5 py-3 text-[10px] uppercase tracking-wider">TOTAL</td>
              <td className="px-5 py-3 text-right tabular-nums text-slate-700">
                {data.totals.original > 0 ? formatINR(data.totals.original) : "—"}
              </td>
              <td className="px-5 py-3 text-right tabular-nums text-slate-900" data-testid="budget-grid-total">
                {formatINR(totalDraft)}
              </td>
              <td className="px-5 py-3 text-right text-xs text-slate-500">
                {data.totals.original > 0
                  ? `${(((totalDraft - data.totals.original) / data.totals.original) * 100).toFixed(1)}%`
                  : "—"}
              </td>
              <td colSpan={3}></td>
            </tr>
          </tbody>
        </table></div>
      </Card>

      {/* Sticky save / discard bar */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm px-5 py-3 flex items-center justify-between gap-3 sticky bottom-4">
        <div className="text-xs text-slate-600">
          {draftChanged
             ? <span className="text-rose-600 font-bold">Unsaved changes — click Save to send for approval</span>
             : <span className="text-slate-500">No changes</span>}
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={load}
            disabled={saving || !draftChanged}
            className="text-xs uppercase tracking-wider font-bold text-slate-600 hover:text-slate-900 px-4 py-2 border border-slate-300"
            data-testid="budget-discard-btn"
          >
            Discard
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !draftChanged}
            className="bg-[#F97316] text-white text-xs uppercase tracking-wider font-bold px-5 py-2 disabled:opacity-40"
            data-testid="budget-save-btn"
          >
            {saving ? "Saving…" : "Save & Submit for Approval"}
          </button>
        </div>
      </div>

      {/* History drawer modal */}
      {historyFor && (
        <BudgetHistoryModal data={historyFor} onClose={() => setHistoryFor(null)} />
      )}
    </div>
  );
}

function BudgetStatusBadge({ status, revision }) {
  const map = {
    "NotConfigured": { cls: "bg-slate-100 text-slate-500", txt: "Not Set" },
    "Draft":         { cls: "bg-amber-100 text-amber-800", txt: "Draft" },
    "Pending":       { cls: "bg-blue-100 text-blue-800",   txt: "Pending" },
    "Approved":      { cls: "bg-emerald-100 text-emerald-800", txt: `Approved · r${revision}` },
    "Rejected":      { cls: "bg-rose-100 text-rose-800",   txt: "Rejected" },
  };
  const m = map[status] || map.Draft;
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold ${m.cls}`}>
      {m.txt}
    </span>
  );
}

function BudgetHistoryModal({ data, onClose }) {
  useUserDirectory();
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
         data-testid="budget-history-modal">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[80vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
              Revision History
            </div>
            <div className="font-display text-lg font-black text-slate-900">
              {data.cost_category}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              Original ₹ {(data.original_amount || 0).toLocaleString("en-IN")}{" "}
              · Latest ₹ {(data.revised_amount || 0).toLocaleString("en-IN")}{" "}
              · {data.revision_history?.length || 0} entries
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="budget-history-close">
            <X size={20} />
          </button>
        </div>
        <div className="overflow-x-auto"><table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-left text-[10px] uppercase tracking-wider font-bold text-slate-600">
              <th className="px-5 py-2">#</th>
              <th className="px-5 py-2">Date</th>
              <th className="px-5 py-2 text-right">Amount (₹)</th>
              <th className="px-5 py-2">Remarks</th>
              <th className="px-5 py-2">Requested By</th>
              <th className="px-5 py-2">Approved By</th>
              <th className="px-5 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {(data.revision_history || []).slice().reverse().map((h, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className="px-5 py-2 font-bold">r{h.revision_no}</td>
                <td className="px-5 py-2">{(h.revision_date || "").slice(0, 10)}</td>
                <td className="px-5 py-2 text-right tabular-nums">{(h.amount || 0).toLocaleString("en-IN")}</td>
                <td className="px-5 py-2 text-xs">{h.remarks || "—"}</td>
                <td className="px-5 py-2 text-xs"><UserLabel value={h.requested_by} fallback="—" /></td>
                <td className="px-5 py-2 text-xs"><UserLabel value={h.approved_by} fallback="—" /></td>
                <td className="px-5 py-2"><BudgetStatusBadge status={h.status} revision={h.revision_no} /></td>
              </tr>
            ))}
            {(data.revision_history || []).length === 0 && (
              <tr><td colSpan={7} className="text-center text-slate-400 py-8">No revision history yet.</td></tr>
            )}
          </tbody>
        </table></div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Sprint 2026-Q1d / Asset Depreciation Step ③ — Project Closure Wizard tab
// ─────────────────────────────────────────────────────────────────────────────
// Single-pane workflow that swaps between TWO views based on current status:
//   • status != "Completed"  →  CLOSE flow   : pre-check buckets + reason +
//                                              acknowledge + commit
//   • status == "Completed"  →  REOPEN flow  : reason + reopen button
// Both views share the "Audit History" panel at the bottom, which paginates
// the append-only `project_closures` collection newest-first. The wizard
// NEVER hides warnings — they remain visible even after closure, so users
// can see what was outstanding when the project was closed (and the badge
// on the listing keeps showing until the source items are resolved).

const CLOSURE_BUCKETS = [
  { key: "open_returnables",         label: "Open Returnables",         hint: "Issue voucher items not yet returned",      testid: "bucket-open-returnables" },
  { key: "open_repairs",             label: "Open Repair Jobs",         hint: "Repairs not yet Marked Ready",              testid: "bucket-open-repairs" },
  { key: "unpaid_purchase_invoices", label: "Unpaid Purchase Invoices", hint: "Approved PIs with payment pending",         testid: "bucket-unpaid-pi" },
  { key: "open_estimates",           label: "Open Estimates",           hint: "Estimates not yet converted / rejected",    testid: "bucket-open-estimates" },
  { key: "issued_assets",            label: "Issued Assets",            hint: "Assets still on rent / out with the team",  testid: "bucket-issued-assets" },
  { key: "budget_breached",          label: "Budget Breach",            hint: "Consumption has exceeded approved budget",  testid: "bucket-budget-breached" },
];

function ProjectClosureWizardTab({ project, onClose, onSaved }) {
  const [warnings, setWarnings] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [ack, setAck] = useState(false);
  const [setStatusOnClose, setSetStatusOnClose] = useState(true);
  const [reopenReason, setReopenReason] = useState("");
  // Track the latest known status — local snapshot updated after every
  // commit / reopen so the wizard switches modes without a re-render
  // round-trip via the parent.
  const [currentStatus, setCurrentStatus] = useState(project.status || "Active");

  const refresh = async () => {
    try {
      setLoading(true);
      const [w, h] = await Promise.all([
        api.get(`/project-closures/${project.id}/warnings`),
        api.get(`/project-closures/${project.id}/history`),
      ]);
      setWarnings(w.data || null);
      setHistory(h.data || []);
      if (w.data?.current_status) setCurrentStatus(w.data.current_status);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load closure data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [project.id]);

  const total = warnings?.total || 0;
  const isCompleted = (currentStatus || "").toLowerCase() === "completed";

  const handleClose = async () => {
    if (!reason.trim()) {
      toast.error("Please provide a closure reason.");
      return;
    }
    if (total > 0 && !ack) {
      toast.error("Tick 'Acknowledge & proceed' to close with open items.");
      return;
    }
    try {
      setBusy(true);
      await api.post(`/project-closures/${project.id}/commit`, {
        reason: reason.trim(),
        acknowledge: ack,
        set_status: setStatusOnClose,
      });
      toast.success(`Project ${project.project_code || ""} closed.`);
      setReason("");
      setAck(false);
      await refresh();
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Closure failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleReopen = async () => {
    if (!reopenReason.trim()) {
      toast.error("Please provide a reopen reason.");
      return;
    }
    try {
      setBusy(true);
      await api.post(`/project-closures/${project.id}/reopen`, {
        reason: reopenReason.trim(),
      });
      toast.success(`Project ${project.project_code || ""} reopened.`);
      setReopenReason("");
      await refresh();
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reopen failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="project-closure-wizard-tab">
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow={`Project · ${project.project_code || ""}`}
        title={`Closure Wizard — ${project.project_name || ""}`}
        subtitle={`${project.client_name || ""} · ${project.branch_name || ""} · Status: ${currentStatus}`}
        actions={
          <button
            type="button"
            onClick={onClose}
            className="text-xs uppercase tracking-wider text-slate-500 hover:text-slate-900"
            data-testid="closure-wizard-close-btn"
          >
            <X size={16} className="inline mr-1" /> Close
          </button>
        }
      />

      {loading ? (
        <div className="text-center text-slate-500 py-12">Loading closure check…</div>
      ) : (
        <>
          {/* Summary banner — total open items + status pill */}
          <Card className="overflow-hidden">
            <div className="px-5 py-4 flex items-center justify-between border-b border-slate-100">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
                  Pre-Closure Health Check
                </div>
                <div className="font-display text-base font-black text-slate-900 mt-0.5">
                  {total === 0
                    ? "All clear — no open items detected."
                    : `${total} open item(s) flagged for review.`}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {total === 0 ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 rounded"
                        data-testid="closure-health-clear">
                    <CheckCircle2 size={12} /> Healthy
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider font-bold bg-amber-100 text-amber-800 border border-amber-300 rounded"
                        data-testid="closure-health-warnings">
                    <AlertTriangle size={12} /> {total} Warning(s)
                  </span>
                )}
                <button
                  type="button"
                  onClick={refresh}
                  disabled={loading}
                  className="text-[10px] uppercase tracking-wider font-bold text-slate-500 hover:text-slate-900 px-2 py-1 border border-slate-300 rounded"
                  data-testid="closure-refresh-btn"
                  title="Re-run the diagnostic — picks up resolutions made in other tabs"
                >
                  <RotateCcw size={11} className="inline mr-1" /> Re-Run
                </button>
              </div>
            </div>

            {/* Bucket cards */}
            <div className="grid md:grid-cols-3 gap-px bg-slate-100">
              {CLOSURE_BUCKETS.map(b => {
                const bucket = warnings?.buckets?.[b.key] || { count: 0, samples: [] };
                const cnt = bucket.count || 0;
                return (
                  <div key={b.key}
                       className="bg-white p-4 min-h-[120px]"
                       data-testid={b.testid}>
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{b.label}</div>
                      <div className={`text-xl font-black tabular-nums ${cnt > 0 ? "text-amber-700" : "text-emerald-700"}`}
                           data-testid={`${b.testid}-count`}>
                        {cnt}
                      </div>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{b.hint}</div>
                    {cnt > 0 && (
                      <div className="mt-2 space-y-0.5 text-[11px] text-slate-700">
                        {(bucket.samples || []).slice(0, 3).map((s, i) => (
                          <div key={i} className="truncate" title={JSON.stringify(s)}>
                            • {_bucketSampleLabel(b.key, s)}
                          </div>
                        ))}
                        {cnt > 3 && (
                          <div className="text-slate-400 text-[10px]">+{cnt - 3} more…</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Action card — either CLOSE or REOPEN depending on status */}
          {!isCompleted ? (
            <Card className="p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
                Close This Project
              </div>
              <div className="font-display text-base font-black text-slate-900 mt-0.5">
                Mark the engagement as Completed and write a closure audit row.
              </div>
              <div className="mt-3 space-y-3 max-w-2xl">
                <label className="block">
                  <div className="text-[11px] uppercase tracking-wider font-bold text-slate-700 mb-1">
                    Closure Reason <span className="text-rose-500">*</span>
                  </div>
                  <textarea
                    rows={3}
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    placeholder="e.g. Contract complete, all deliverables signed off, retention period closed."
                    className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#F97316]"
                    data-testid="closure-reason-input"
                  />
                </label>
                {total > 0 && (
                  <label className="flex items-start gap-2 text-sm text-slate-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded">
                    <input
                      type="checkbox"
                      checked={ack}
                      onChange={e => setAck(e.target.checked)}
                      className="mt-0.5"
                      data-testid="closure-ack-checkbox"
                    />
                    <span>
                      <b>Acknowledge & proceed</b> — I understand this project still has {total} open item(s) above; close anyway.
                    </span>
                  </label>
                )}
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={setStatusOnClose}
                    onChange={e => setSetStatusOnClose(e.target.checked)}
                    data-testid="closure-setstatus-checkbox"
                  />
                  <span>Also mark project status as <b>Completed</b> (recommended).</span>
                </label>
                <div className="pt-2">
                  <button
                    type="button"
                    disabled={busy || !reason.trim() || (total > 0 && !ack)}
                    onClick={handleClose}
                    className="bg-[#F97316] hover:bg-[#EA580C] disabled:bg-slate-300 disabled:cursor-not-allowed text-white px-5 py-2.5 text-xs uppercase tracking-wider font-bold"
                    data-testid="closure-commit-btn"
                  >
                    {busy ? "Closing…" : "Close Project"}
                  </button>
                </div>
              </div>
            </Card>
          ) : (
            <Card className="p-5 border-2 border-emerald-200 bg-emerald-50/40">
              <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-bold flex items-center gap-1">
                <CheckCircle2 size={12} /> Project Closed
              </div>
              <div className="font-display text-base font-black text-slate-900 mt-0.5">
                This project is currently marked <b>Completed</b>. Reopen it if work resumes.
              </div>
              <div className="mt-3 space-y-3 max-w-2xl">
                <label className="block">
                  <div className="text-[11px] uppercase tracking-wider font-bold text-slate-700 mb-1">
                    Reopen Reason <span className="text-rose-500">*</span>
                  </div>
                  <textarea
                    rows={3}
                    value={reopenReason}
                    onChange={e => setReopenReason(e.target.value)}
                    placeholder="e.g. Additional change-order received; reopening for billing."
                    className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#F97316]"
                    data-testid="reopen-reason-input"
                  />
                </label>
                <div>
                  <button
                    type="button"
                    disabled={busy || !reopenReason.trim()}
                    onClick={handleReopen}
                    className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white px-5 py-2.5 text-xs uppercase tracking-wider font-bold"
                    data-testid="reopen-commit-btn"
                  >
                    <RotateCcw size={12} className="inline mr-1" />
                    {busy ? "Reopening…" : "Reopen Project"}
                  </button>
                </div>
              </div>
            </Card>
          )}

          {/* Audit history */}
          <Card>
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <History size={14} className="text-slate-500" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
                  Closure Audit History
                </div>
                <div className="font-display text-base font-black text-slate-900 mt-0.5">
                  Append-only log of every close / reopen event
                </div>
              </div>
            </div>
            {history.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-400" data-testid="closure-history-empty">
                No closure events yet for this project.
              </div>
            ) : (
              <div className="overflow-x-auto"><table className="w-full min-w-[640px] text-sm" data-testid="closure-history-table">
                <thead>
                  <tr className="text-left bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-600 font-bold">
                    <th className="px-5 py-2">When</th>
                    <th className="px-5 py-2">Action</th>
                    <th className="px-5 py-2">Status</th>
                    <th className="px-5 py-2">Warnings</th>
                    <th className="px-5 py-2">By</th>
                    <th className="px-5 py-2">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h, i) => (
                    <tr key={h.id || i} className="border-b border-slate-100"
                        data-testid={`closure-history-row-${i}`}>
                      <td className="px-5 py-2 text-xs text-slate-700 whitespace-nowrap">
                        {(h.performed_at || "").slice(0, 19).replace("T", " ")}
                      </td>
                      <td className="px-5 py-2">
                        {h.action === "closed" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold bg-rose-100 text-rose-800 border border-rose-300 rounded">
                            Closed
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold bg-indigo-100 text-indigo-800 border border-indigo-300 rounded">
                            Reopened
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-2 text-xs text-slate-700">
                        {h.previous_status || "—"} → {h.new_status || "—"}
                      </td>
                      <td className="px-5 py-2 text-xs text-slate-700 tabular-nums">
                        {h.action === "closed"
                          ? (h.warning_total > 0
                              ? <span className="text-amber-700 font-bold">{h.warning_total}{h.acknowledged ? " (ack)" : ""}</span>
                              : <span className="text-emerald-700">0</span>)
                          : "—"}
                      </td>
                      <td className="px-5 py-2 text-xs text-slate-700">{h.performed_by || "—"}</td>
                      <td className="px-5 py-2 text-xs text-slate-600">{h.reason || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

// Helper: render a one-line label for a bucket sample row. Kept tiny —
// the wizard only shows up to 3 samples per bucket, so anything more
// detailed would crowd the card.
function _bucketSampleLabel(bucketKey, s) {
  switch (bucketKey) {
    case "open_returnables":
      return `${s.issue_voucher_number || "—"} · ${s.item_code || s.asset_code || "item"} · pending ${s.qty_pending ?? "?"}`;
    case "open_repairs":
      return `${s.voucher_number || "—"} · ${s.asset_code || s.asset_name || "asset"} @ ${s.vendor_name || "vendor"}`;
    case "unpaid_purchase_invoices":
      return `${s.invoice_number || "—"} · ${s.vendor_name || ""} · ₹${(s.total_amount || 0).toLocaleString("en-IN")} · ${s.payment_status || ""}`;
    case "open_estimates":
      return `${s.estimate_number || "—"} · ${s.client_name || ""} · ${s.status || ""}`;
    case "issued_assets":
      return `${s.asset_code || "—"} · ${s.name || ""} · ${s.status || ""}`;
    case "budget_breached":
      return `Consumed ₹${(s.consumed || 0).toLocaleString("en-IN")} vs Budget ₹${(s.budget || 0).toLocaleString("en-IN")} (over by ₹${(s.over_by || 0).toLocaleString("en-IN")})`;
    default:
      return JSON.stringify(s).slice(0, 80);
  }
}
